import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type { ExportColumns, ExportRequest } from "@/model/interface/DataExport";
import { useCreateExportMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  request: ExportRequest;
  allowColumnChoice?: boolean;
}

export default function ExportDialog({
  open,
  onClose,
  title,
  description,
  request,
  allowColumnChoice = false,
}: ExportDialogProps) {
  const [columns, setColumns] = useState<ExportColumns>("ALL");
  const [queued, setQueued] = useState(false);
  const [createExport, { isLoading: isStarting }] = useCreateExportMutation();

  function closeDialog() {
    onClose();
    setQueued(false);
    setColumns("ALL");
  }

  async function startExport() {
    try {
      await createExport({ ...request, columns: allowColumnChoice ? columns : "ALL" }).unwrap();
      setQueued(true);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The export could not start"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? closeDialog() : null)}>
      <DialogContent className="max-w-md text-foreground">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!queued && allowColumnChoice && (
          <RadioGroup value={columns} onValueChange={(value) => setColumns(value as ExportColumns)} className="gap-3">
            <div className="flex items-start gap-2">
              <RadioGroupItem value="ALL" id="export-columns-all" className="mt-0.5" />
              <Label htmlFor="export-columns-all" className="space-y-0.5 font-normal">
                <span className="block text-sm font-medium">All columns</span>
                <span className="block text-xs text-muted-foreground">
                  Every field, phone, email and custom field. The file can be imported back without duplicates.
                </span>
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem value="VISIBLE" id="export-columns-visible" className="mt-0.5" />
              <Label htmlFor="export-columns-visible" className="space-y-0.5 font-normal">
                <span className="block text-sm font-medium">Visible columns</span>
                <span className="block text-xs text-muted-foreground">The columns shown in the list.</span>
              </Label>
            </div>
          </RadioGroup>
        )}

        {queued && (
          <p className="text-sm text-muted-foreground">
            Your export is queued. Exports run at the top of every hour, and you get a notification and an email when
            the file is ready. It stays available for 7 days on the{" "}
            <Link to="/manage-organisation/settings/export" onClick={closeDialog} className="underline underline-offset-4">
              Export page
            </Link>{" "}
            in General settings.
          </p>
        )}

        <DialogFooter>
          {queued ? (
            <Button onClick={closeDialog}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button onClick={startExport} disabled={isStarting}>
                {isStarting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Export
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
